import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from '../api.service';
import { FrontendLoggerService } from '../frontend-logger.service';
import { ExpertRole } from '../../models/expert-role.model';

/**
 * Acceso al catálogo de roles expertos activos (Role-Based Queries).
 * Consume `GET /api/expert-roles` a través de `ApiService` (misma base URL que `ChatService`).
 * Si el backend falla o la feature no está habilitada, degrada a lista vacía para no romper el chat.
 */
@Injectable({
  providedIn: 'root'
})
export class ExpertRolesService {
  constructor(
    private readonly api: ApiService,
    private readonly frontendLogger: FrontendLoggerService
  ) {}

  getRoles(): Observable<ExpertRole[]> {
    return this.api.get<unknown>('expert-roles').pipe(
      map((response) => this.mapRoles(response)),
      catchError((error: unknown) => {
        this.frontendLogger.warn(
          'No se pudieron obtener los roles expertos; se continúa sin selector: ' + this.formatError(error),
          'ExpertRolesService.getRoles'
        );
        return of<ExpertRole[]>([]);
      })
    );
  }

  private mapRoles(response: unknown): ExpertRole[] {
    if (!Array.isArray(response)) {
      return [];
    }

    return response.reduce<ExpertRole[]>((acc, item) => {
      const role = this.mapRole(item);
      if (role) {
        acc.push(role);
      }
      return acc;
    }, []);
  }

  private mapRole(item: unknown): ExpertRole | null {
    if (!item || typeof item !== 'object') {
      return null;
    }

    const record = item as Record<string, unknown>;
    const code = typeof record['code'] === 'string' ? record['code'] : null;
    const displayName = typeof record['displayName'] === 'string' ? record['displayName'] : null;
    if (!code || !displayName) {
      return null;
    }

    // Solo se incluyen los campos opcionales cuando existen: no emitir claves con
    // valor undefined (mantiene el objeto limpio y comparaciones estrictas estables).
    const role: ExpertRole = { code, displayName };
    if (typeof record['description'] === 'string') {
      role.description = record['description'];
    }
    if (typeof record['icon'] === 'string') {
      role.icon = record['icon'];
    }
    if (typeof record['enrichmentHint'] === 'string') {
      role.enrichmentHint = record['enrichmentHint'];
    }
    return role;
  }

  private formatError(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }

    if (error && typeof error === 'object' && 'message' in error) {
      return String((error as { message?: unknown }).message || error);
    }

    return String(error);
  }
}
